import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Notification } from 'types';

import { fetchNotifications } from './thunks';
import { MarkAsReadPayload, NotificationsState } from './types';

const initialState: NotificationsState = {
  count: 0,
  unreadCount: 0,
  isLoading: false,
  notifications: [],
};

const reset = (state: NotificationsState) => {
  state.count = 0;
  state.unreadCount = 0;
  state.isLoading = false;
  state.notifications = [];
};

const notificationsSlice = createSlice({
  initialState,
  name: 'notifications',
  extraReducers: (builder) => {
    builder.addCase(fetchNotifications.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchNotifications.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.count = payload.count;
      state.notifications = payload.rows;
      state.unreadCount = payload.unreadCount;
    });

    builder.addCase(fetchNotifications.rejected, reset);
  },
  reducers: {
    clearNotifications: (state) => {
      reset(state);
    },

    markAllAsRead: (state) => {
      state.unreadCount = 0;
      state.notifications = state.notifications.map((notification) => ({
        ...notification,
        isRead: true,
      }));
    },

    addNotification: (state, { payload }: PayloadAction<Notification>) => {
      state.notifications = [payload, ...state.notifications];
      if (!payload.isRead) {
        state.unreadCount += 1;
      }
    },

    markAsRead: (state, { payload }: PayloadAction<MarkAsReadPayload>) => {
      const index = state.notifications.findIndex(
        (notification) => notification.id === payload.id
      );

      if (index !== -1) {
        state.notifications[index].isRead = payload.readStatus;
        state.unreadCount = payload.readStatus
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount + 1;
      }
    },

    removeNotification: (state, { payload }: PayloadAction<number>) => {
      const index = state.notifications.findIndex(
        (notification) => notification.id === payload
      );

      if (index !== -1) {
        const isUnread = !state.notifications[index].isRead;
        state.notifications.splice(index, 1);

        if (isUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    },
  },
});

const { reducer: notificationsReducer } = notificationsSlice;
const {
  markAsRead,
  markAllAsRead,
  addNotification,
  removeNotification,
  clearNotifications,
} = notificationsSlice.actions;

export {
  markAsRead,
  markAllAsRead,
  addNotification,
  removeNotification,
  clearNotifications,
  notificationsReducer,
};

export * from './thunks';
export * from './selectors';
